import {
  NDripPiezometer,
  PulseSensor,
  WFRSensor,
  SoilTempSensor,
} from '../model/GeneralClasses';
import { convert } from './unitsManager';
import * as sensorsManager from './sensorsManager';
import * as weatherManager from './weatherManager';
import { getRelevantCyclicDateByDate, error_message, upper } from './helpers';
import { plannerType } from './planningManager';
import { getMixpanelInstance } from './mixpanelManager';

var axios = require('axios');
var GeneralClasses = require('@model/GeneralClasses');
var React = require('react');
const daysjs = require('../utils/dayjsConfig');
const geoarea = require('geo-area')(/*options*/ { x: 'lng', y: 'lat' });
const {
  normalizeKcGrowthPhasesForPayload,
} = require('../utils/kcPhaseNormalize');

const SCIROOT_FIELD_PATH = 'field';
const SCIROOT_FIELD_RECORDS_PATH = 'field_records';
const RECOMMENDATION_PATH = 'recommendations';

export const STRESSED_LIMIT = 0.4;

// How a field's irrigation recommendation is presented to the user.
export const recommendationDisplay = {
  DURATION: 'duration', // hours
  AMOUNT: 'amount', // mm / inch
};

// Irrigation types whose recommendation is a duration by default. Kept in sync
// with DURATION_BASED_IRRIGATION_TYPES in the API (FieldManager.py).
export const DURATION_BASED_IRRIGATION_TYPES = [
  'ndrip',
  'seepage',
  'flood',
  'furrow',
];

// The display a field gets when the user has not chosen one explicitly — also
// what a field switches to when its irrigation type changes.
export const defaultRecommendationDisplay = irrigation_type =>
  DURATION_BASED_IRRIGATION_TYPES.includes(
    (irrigation_type || '').toLowerCase()
  )
    ? recommendationDisplay.DURATION
    : recommendationDisplay.AMOUNT;

export const stress_levels_1 = {
  critical: {
    min_value: 0.0,
    max_value: 0.15,
    color: 'rgb(246, 107, 122)',
    background: '#FFF5F6',
    message: 'stress_level_very_high',
    commandMessage: 'irrigate_command',
    group: 1,
  },
  stressed: {
    min_value: 0.15,
    max_value: STRESSED_LIMIT,
    color: 'rgb(255, 179, 2)',
    background: '#FFFBF2',
    message: 'stress_level_high',
    commandMessage: 'irrigate_command',
    group: 2,
  },
  getting_closer: {
    min_value: STRESSED_LIMIT,
    max_value: 0.5,
    color: 'rgb(10, 203, 188)',
    background: '#E4F8F6',
    message: 'stress_level_getting_low',
    commandMessage: 'get_ready_to_irrigate',
    group: 3,
  },
  valid: {
    min_value: 0.5,
    max_value: 1.01,
    color: 'rgb(10, 203, 188)',
    background: '#E4F8F6',
    message: 'stress_level_no_stress',
    commandMessage: 'dont_irrigate_command',
    group: 4,
  },
};

export const stress_levels_v2 = {
  critical: {
    min_value: 0.0,
    max_value: 0.15,
    color: 'rgb(246, 107, 122)',
    background: '#F66B7A',
    message: 'gauge_low_v3',
    group: 1,
  },
  under_irrigation: {
    min_value: 0.15,
    max_value: STRESSED_LIMIT,
    color: 'rgb(255, 179, 2)',
    background: '#FFB302',
    message: 'gauge_medium_v3',
    group: 2,
  },
  sufficiency_irrigation: {
    min_value: STRESSED_LIMIT,
    max_value: 1.01,
    color: 'rgb(10, 203, 188)',
    background: '#0ACBBC',
    message: 'gauge_high_v3',
    group: 3,
  },
};

export class FieldDataItem {
  constructor(value, date) {
    this.value = value;
    this.date = date;
  }
}

export class FieldDataItems {
  constructor(irrigationDataItem, stressDataItem, accumulatedETc) {
    this.irrigationDataItem = irrigationDataItem;
    this.stressDataItem = stressDataItem;
    this.accumulatedETc = accumulatedETc;
  }
}

export var isRecommendationOutOfDate = date => {
  var day_diff = daysjs().diff(date, 'hours');

  if (day_diff > 24) {
    return true;
  } else {
    return false;
  }
};

export var last_recommendation_date = field => {
  var wp_date_last = '1494112409';
  if (field.historical_data.ir_date) {
    var wp_date_keys = Object.keys(field.historical_data.ir_date.values);
    wp_date_keys.sort();
    wp_date_keys.reverse();

    wp_date_last = field.historical_data.ir_date.values[wp_date_keys[0]];
  }

  return wp_date_last;
};

export var last_recommendation = field => {
  let recommendationDataItem = new FieldDataItem(-1.0, 'N/A');
  if (
    field &&
    field.historical_data &&
    field.historical_data.ir &&
    Object.keys(field.historical_data.ir.values).length > 0
  ) {
    var keys = Object.keys(field.historical_data.ir.values);
    keys.sort();
    keys.reverse();

    var last = field.historical_data.ir.values[keys[0]];
    var wp_date_last = last_recommendation_date(field);

    if (!isRecommendationOutOfDate(daysjs.unix(wp_date_last))) {
      recommendationDataItem.value = parseFloat(last);
      recommendationDataItem.date = wp_date_last;
    }
  }

  return recommendationDataItem;
};

export var last_accu_etc = field => {
  let recommendationDataItem = new FieldDataItem(-1.0, 'N/A');
  if (
    field &&
    field.historical_data &&
    field.historical_data.accu_etc &&
    Object.keys(field.historical_data.accu_etc.values).length > 0
  ) {
    var keys = Object.keys(field.historical_data.accu_etc.values);
    keys.sort();
    keys.reverse();

    var last = field.historical_data.accu_etc.values[keys[0]];
    var wp_date_last = last_recommendation_date(field);

    if (!isRecommendationOutOfDate(daysjs.unix(wp_date_last))) {
      recommendationDataItem.value = parseFloat(last);
      recommendationDataItem.date = wp_date_last;
    }
  }

  return recommendationDataItem;
};

export var getSelectedFieldArea = (field, userAreaUnits) => {
  var area = field.geo_data.area_size;
  if (area === undefined) {
    area = getFieldArea(field.geo_data.areas, userAreaUnits);
  }
  return convert(parseFloat(area), 'dunam', userAreaUnits, 'area');
};

export var getFieldArea = fieldAreas => {
  let areas = [];
  let polygon = undefined;

  for (let currentArea in fieldAreas) {
    polygon = [];
    for (let currentCoordinate in fieldAreas[currentArea]) {
      let curr = fieldAreas[currentArea][currentCoordinate];
      polygon.push({
        lng: curr.longitude,
        lat: curr.latitude,
      });
    }
    // calculate and convert to dunam from squared meter
    let area = geoarea(polygon) / 1000.0;
    areas.push(area);
  }

  var sum = areas.reduce(function (a, b) {
    return a + b;
  }, 0);

  return sum.toFixed(2);
};

export const last_kc = field => {
  if (
    !field?.historical_data?.kc?.values ||
    Object.keys(field.historical_data.kc.values).length === 0
  ) {
    return ['N/A', 'N/A'];
  }

  const keys = Object.keys(field.historical_data.kc.values).map(Number);
  const latestTimestamp = Math.max(...keys);

  const today = daysjs().tz(field.geo_data.time_zone).startOf('day');
  const latestDate = daysjs.unix(latestTimestamp);
  const dateString = today.isSame(latestDate, 'day')
    ? '(today)'
    : `(${latestDate.format('YYYY-MM-DD')})`;

  const last = field.historical_data.kc.values[latestTimestamp];

  return [last, dateString];
};

export var last_stress_level = field => {
  let stressDataItem = new FieldDataItem(-1.0, 'N/A');

  if (
    field &&
    field.historical_data &&
    field.historical_data.adjusted_stress &&
    Object.keys(field.historical_data.adjusted_stress.values).length > 0
  ) {
    var keys = Object.keys(field.historical_data.adjusted_stress.values);
    keys.sort();
    keys.reverse();

    var wp_date_last = last_recommendation_date(field);

    if (!isRecommendationOutOfDate(daysjs.unix(wp_date_last))) {
      var last_stress_level = parseFloat(
        field.historical_data.adjusted_stress.values[keys[0]]
      );

      if (last_stress_level > 0) {
        last_stress_level =
          STRESSED_LIMIT + (1 - STRESSED_LIMIT) * last_stress_level;
      } else {
        last_stress_level = STRESSED_LIMIT * (1 + last_stress_level);
      }

      stressDataItem.value = parseFloat(last_stress_level);
      stressDataItem.date = wp_date_last;
    }
  }

  return stressDataItem;
};

// The "unstressed but still recommending irrigation -> show critical" override
// used to live here. It now lives on the backend, baked into the stored
// adjusted_stress (see IrrigationManager.apply_recommendation_stress_override),
// so the whole stress decision is server-side and fixable without a client
// release. The gauge just renders the stored value.
export var getFieldItems = field => {
  var lastIrrigationRecommendation = last_recommendation(field);
  let lastStressLevel = last_stress_level(field);
  let lastAccuETc = last_accu_etc(field);

  return new FieldDataItems(
    lastIrrigationRecommendation,
    lastStressLevel,
    lastAccuETc
  );
};

// Helper to select correct stress levels
export function getStressLevels(stress_v2) {
  return stress_v2 ? stress_levels_v2 : stress_levels_1;
}

// No color for missing data (transparent/blank)
export const MISSING_DATA_COLOR = 'transparent';

// Update get_stress_color to accept field
export var get_stress_color = (stress_level, has_planner_by_weeks) => {
  const stress_levels = getStressLevels(has_planner_by_weeks);
  for (var key in stress_levels) {
    var current_stress_record = stress_levels[key];
    if (
      stress_level >= current_stress_record['min_value'] &&
      stress_level < current_stress_record['max_value']
    ) {
      return current_stress_record['color'];
    }
  }
  return MISSING_DATA_COLOR;
};

// Update get_stress_group to accept field
export var get_stress_group = (stress_level, field) => {
  const stress_levels = getStressLevels(isSupportingPlannerByWeeks(field));
  for (var key in stress_levels) {
    var current_stress_record = stress_levels[key];
    if (
      stress_level >= current_stress_record['min_value'] &&
      stress_level < current_stress_record['max_value']
    ) {
      return current_stress_record['group'];
    }
  }
  return 5;
};

// Update compareFields to accept fields
// Uses numeric subtraction for consistent comparisons (required by Hermes strict sort)
export var compareFields = (_f1, _f2) => {
  let f1StressGroup = get_stress_group(_f1.stressLevel, _f1.field);
  let f2StressGroup = get_stress_group(_f2.stressLevel, _f2.field);
  //primary sort - by stress group (stress level categorized)
  if (f1StressGroup !== f2StressGroup) {
    return f1StressGroup - f2StressGroup;
  }
  // If both fields have no data (group 5), sort alphabetically by name
  if (f1StressGroup === 5) {
    return (_f1.name || '').localeCompare(_f2.name || '');
  }
  //secondary sort - by accumulated ETC (higher first)
  const etc1 = _f1.accumulatedETc ?? -Infinity;
  const etc2 = _f2.accumulatedETc ?? -Infinity;
  if (etc1 !== etc2) {
    return etc2 - etc1;
  }
  //third sort - by water Potential samples (fields with data first)
  const hasWp1 = _f1.wpSamples !== undefined ? 0 : 1;
  const hasWp2 = _f2.wpSamples !== undefined ? 0 : 1;
  if (hasWp1 !== hasWp2) {
    return hasWp1 - hasWp2;
  }
  //final sort - by name to keep order stable across edits/refreshes
  return (_f1.name || '').localeCompare(_f2.name || '');
};

// Update get_stress_background_color to accept field
export var get_stress_background_color = (
  stress_level,
  has_planner_by_weeks
) => {
  const stress_levels = getStressLevels(has_planner_by_weeks);
  for (var key in stress_levels) {
    var current_stress_record = stress_levels[key];
    if (
      stress_level >= current_stress_record['min_value'] &&
      stress_level < current_stress_record['max_value']
    ) {
      return current_stress_record['background'];
    }
  }
  return MISSING_DATA_COLOR;
};

// Update get_stress_message to accept field
export var get_stress_message = (
  stress_level,
  irrigation_recommendation,
  accumulated_etc,
  recommendation_date,
  units,
  has_planner_by_weeks // for planner with type plannerType.WEEKS
) => {
  let messageObject = {
    mainMessage: '',
    commandMessage: '',
    irrigationAmountMessage: '',
  };
  if (irrigation_recommendation == -1) {
    messageObject['mainMessage'] = 'no_recommendation_available';
    return messageObject;
  }
  const stress_levels = getStressLevels(has_planner_by_weeks);
  for (var key in stress_levels) {
    var current_stress_record = stress_levels[key];
    if (
      stress_level >= current_stress_record['min_value'] &&
      stress_level < current_stress_record['max_value']
    ) {
      // For new logic, just show the text, not the number
      if (has_planner_by_weeks) {
        messageObject['irrigationAmountMessage'] = '';
        messageObject['commandMessage'] = '';
        messageObject['mainMessage'] = current_stress_record['message'];
        return messageObject;
      } else {
        if (key == 'critical' || key == 'stressed') {
          messageObject['irrigationAmountMessage'] =
            irrigation_recommendation + ' ' + units;
        } else if (accumulated_etc == 0) {
          messageObject['irrigationAmountMessage'] = 'good';
        } else {
          messageObject['irrigationAmountMessage'] =
            accumulated_etc + ' ' + units;
        }
        messageObject['commandMessage'] =
          current_stress_record['commandMessage'];
        messageObject['mainMessage'] = current_stress_record['message'];
        return messageObject;
      }
    }
  }
  return '';
};

var sensorObjectsToDictionary = sensors => {
  let dict = {};

  for (let currentSensorKey in sensors) {
    // Since transmitter id is presented with the port name within the app, we change it before we send it to the server.
    let givenTransmitterId = sensors[currentSensorKey]['transmitter_id'];

    let transmitterId = givenTransmitterId;
    let port = undefined;
    let transmitterIdParts = givenTransmitterId
      ? givenTransmitterId.match(/(.*\d)([^\d]+)?$/)
      : null;
    if (transmitterIdParts) {
      transmitterId = transmitterIdParts[1];
      port = transmitterIdParts[2];
    }

    let coordinates = sensors[currentSensorKey]['coordinates'];
    let name = sensors[currentSensorKey]['name'];
    let sample_interval = sensors[currentSensorKey]['sample_interval'];
    let transmission_interval =
      sensors[currentSensorKey]['transmission_interval'];

    let type = sensors[currentSensorKey]['type'];
    let ignore_in_calculations =
      sensors[currentSensorKey]['ignore_in_calculations'];
    let paused = sensors[currentSensorKey]['paused'];

    let sensorDictionary = {
      coordinates: coordinates,
      name: name,
      type: type,
      transmitter_id: transmitterId,
      port: port,
      ignore_in_calculations: ignore_in_calculations,
      paused: paused,
      transmission_interval: transmission_interval,
      sample_interval: sample_interval,
    };

    if (sensors[currentSensorKey] instanceof GeneralClasses.WFRSensor) {
      sensorDictionary['m3_per_pulse'] =
        sensors[currentSensorKey]['m3_per_pulse'];
      if (
        sensors[currentSensorKey] instanceof GeneralClasses.WFRSensorTalgil ||
        sensors[currentSensorKey] instanceof GeneralClasses.WFRSensorMotorola ||
        sensors[currentSensorKey] instanceof GeneralClasses.WFRSensorGalcon ||
        sensors[currentSensorKey] instanceof
          GeneralClasses.WFRSensorAquastride ||
        sensors[currentSensorKey] instanceof GeneralClasses.WFRSensorNDrip ||
        sensors[currentSensorKey] instanceof GeneralClasses.WFRSensorNDripAI
      ) {
        sensorDictionary['manufacturer'] =
          sensors[currentSensorKey]['manufacturer'];
        sensorDictionary['programs'] = sensors[currentSensorKey]['programs'];
        sensorDictionary['valves'] = sensors[currentSensorKey]['valves'];
        sensorDictionary['sub_type'] = sensors[currentSensorKey]['sub_type'];
      }
      if (!sensors[currentSensorKey]['farming_unit_serial_number']) {
        sensorDictionary['fert_meters'] =
          sensors[currentSensorKey]['fert_meters'];
      }
    } else if (
      sensors[currentSensorKey] instanceof GeneralClasses.PulseSensor
    ) {
      sensorDictionary['m3_per_pulse'] =
        sensors[currentSensorKey]['m3_per_pulse'];
      sensorDictionary['manufacturer'] =
        sensors[currentSensorKey]['manufacturer'];
    } else if (
      sensors[currentSensorKey] instanceof GeneralClasses.NDripPiezometer
    ) {
      sensorDictionary['manufacturer'] =
        sensors[currentSensorKey]['manufacturer'];
      sensorDictionary['should_consider_as_wfr'] =
        sensors[currentSensorKey]['should_consider_as_wfr'];
      sensorDictionary['offset'] = sensors[currentSensorKey]['offset'];
    } else if (
      sensors[currentSensorKey] instanceof GeneralClasses.SoilTempSensor
    ) {
      sensorDictionary['manufacturer'] =
        sensors[currentSensorKey]['manufacturer'];
      sensorDictionary['installation_depth'] =
        sensors[currentSensorKey]['installation_depth'];
    } else if (
      sensors[currentSensorKey] instanceof GeneralClasses.PressureSensor
    ) {
      sensorDictionary['manufacturer'] =
        sensors[currentSensorKey]['manufacturer'];
      sensorDictionary['offset'] = sensors[currentSensorKey]['offset'];
      sensorDictionary['should_consider_as_wfr'] =
        sensors[currentSensorKey]['should_consider_as_wfr'];
      if (
        sensors[currentSensorKey] instanceof
        GeneralClasses.NDripHighPressureSensor
      ) {
        sensorDictionary['boundaries'] =
          sensors[currentSensorKey]['boundaries'];
      }
    } else if (sensors[currentSensorKey] instanceof GeneralClasses.WPSensor) {
      // Tensiometer range ('100cbar'). Left undefined for a 50cbar unit, so
      // JSON.stringify drops the key and sensors already in production are
      // never given a sub_type; an explicit '' clears one that was set.
      sensorDictionary['sub_type'] = sensors[currentSensorKey]['sub_type'];
    }

    let sensorSerialNumber = currentSensorKey;
    dict[sensorSerialNumber] = sensorDictionary;
  }

  return dict;
};

var getSoilDataObject = field_object => {
  return {
    initial_concentrations: {
      N_Concenration_Organic_Matter:
        field_object.initial_concentrations.N_Concenration_Organic_Matter,
      NH4_Concentration: field_object.initial_concentrations.NH4_Concentration,
      NO3_Concentration: field_object.initial_concentrations.NO3_Concentration,
    },
    soil_type: field_object.soil_type,
  };
};

const mapTimeFrames = timeFrames =>
  timeFrames.map(
    ({
      frequency_x,
      active_period_start_date,
      active_period_end_date,
      min,
      max,
    }) => {
      const mapped = {
        frequency_x,
        active_period_start_date,
        active_period_end_date,
      };
      if (min != null) mapped.min = parseFloat(min);
      if (max != null) mapped.max = parseFloat(max);
      return mapped;
    }
  );

const getIrrigationSystem = irrigation_object => {
  return {
    irrigation_type: irrigation_object.irrigation_type,
    irrigation_rate: irrigation_object.irrigation_rate,
    recommendation_display:
      irrigation_object.recommendation_display ||
      defaultRecommendationDisplay(irrigation_object.irrigation_type),
    irrigation_hin: parseFloat(irrigation_object.irrigation_hin) || 0,
    tank_type: irrigation_object.tank_type,
    measured_irrigation_rate: irrigation_object.measured_irrigation_rate,
    fertigation_concentrations: irrigation_object.fertigation_concentrations,
    time_frames: irrigation_object.time_frames.map(timeFrame => {
      return {
        day: timeFrame.day,
        start_hour: timeFrame.start_hour,
        end_hour: timeFrame.end_hour,
        min: parseFloat(timeFrame.min),
        max: parseFloat(timeFrame.max),
        active_period_start_date: timeFrame.active_period_start_date,
        active_period_end_date: timeFrame.active_period_end_date,
        name: timeFrame.name,
      };
    }),
    weekly_time_frames: mapTimeFrames(irrigation_object.weekly_time_frames),
    interval_time_frames: mapTimeFrames(irrigation_object.interval_time_frames),
  };
};

export var getRecommendation = field_id => {
  return new Promise(function (resolve, reject) {
    axios
      .get(SCIROOT_FIELD_PATH + '/' + RECOMMENDATION_PATH + '/' + field_id)
      .then(function (res) {
        resolve(res.data);
      })
      .catch(function (e) {
        console.log(error_message(e));
        reject(e);
      });
  });
};

export var rate_field_recommendation = (
  field_id,
  date_generated,
  local_midnight_week_start,
  is_useful
) => {
  return new Promise(function (resolve, reject) {
    axios
      .post(SCIROOT_FIELD_PATH + '/' + RECOMMENDATION_PATH + '/rate', {
        field_id: field_id,
        date_generated: date_generated,
        local_midnight_week_start: local_midnight_week_start,
        is_useful: is_useful,
      })
      .then(function (res) {
        resolve(res.data);
      })
      .catch(function (e) {
        console.log(error_message(e));
        reject(e);
      });
  });
};

export var submitRecommendationFeedback = async (
  feedbackValue,
  fieldId,
  dateGenerated,
  localMidnightWeekStart,
  recommendationAmount,
  weekRange,
  recommendationMode,
  units
) => {
  try {
    // Send mixpanel event
    const mixpanelInstance = getMixpanelInstance();
    if (mixpanelInstance) {
      mixpanelInstance.track('Weekly Recommendation Feedback', {
        'Feedback Score': feedbackValue === 'yes' ? 'Positive' : 'Negative',
        'Recommedation Amount': recommendationAmount
          ? recommendationAmount + ' ' + units
          : 'N/A',
        'Week Range': weekRange || 'N/A',
        'Recommendation Mode': recommendationMode || 'N/A',
      });
    }

    // Send API call to store feedback in database
    if (fieldId && localMidnightWeekStart) {
      rate_field_recommendation(
        fieldId,
        dateGenerated,
        parseInt(localMidnightWeekStart), // localMidnightWeekStart is already a unix timestamp
        feedbackValue === 'yes'
      );
      console.log('Feedback stored successfully');
    } else {
      console.warn('Missing required parameters for feedback submission');
    }
  } catch (error) {
    console.error('Failed to store feedback:', error);
  }
};

export const trackRecommendationWeekEvent = (
  weekType,
  recommendationAmount,
  recommendationUnits,
  weekRange
) => {
  if (weekType === 'this') {
    return;
  }
  const eventName =
    weekType === 'next'
      ? 'Weekly Recommendation Next Week Clicked'
      : 'Weekly Recommendation Previous Week Clicked';

  const finalAmount = recommendationAmount
    ? recommendationAmount + ' ' + recommendationUnits
    : 'N/A';
  const mixpanelInstance = getMixpanelInstance();

  mixpanelInstance.track(eventName, {
    'Recommendation Amount': finalAmount,
    'Week Range': weekRange,
  });
};

export var create_field = (distribution_id, farming_unit_id, field_object) => {
  return new Promise(function (resolve, reject) {
    var irrigationSystem = JSON.parse(
      JSON.stringify(getIrrigationSystem(field_object.irrigation_system))
    );
    var sensors = JSON.parse(
      JSON.stringify(sensorObjectsToDictionary(field_object.sensors))
    );

    // remove spaces from transmitter_id
    for (let sensorsKey in sensors) {
      sensors[sensorsKey].transmitter_id = (
        sensors[sensorsKey].transmitter_id || ''
      ).replace(/\s+/g, '');
    }

    var geo_data = { areas: field_object.geo_data.areas };
    let crop_data = JSON.parse(JSON.stringify(field_object.crop_data));
    const createKcType = field_object.crop_data.kc_table.type;
    crop_data.kc_table = {
      type: createKcType,
      data: normalizeKcGrowthPhasesForPayload(
        crop_data.kc_table.data[createKcType],
        createKcType
      ),
    };

    let soil_data = getSoilDataObject(field_object);

    axios
      .post(
        SCIROOT_FIELD_PATH + '/' + distribution_id + '/' + farming_unit_id,
        {
          field_name: field_object.name,
          last_days_temperature: 20,
          soil_type: field_object.soil_type,
          soil_data: soil_data,
          irrigation_system: irrigationSystem,
          sensors: sensors,
          cluster: field_object.cluster,
          crop_data: crop_data,
          geo_data: geo_data,
          installation_date: field_object.contract_data.installation_date,
          expiration_date: field_object.contract_data.expiration_date,
          settings: field_object.settings,
          farming_type: field_object.farming_type,
        }
      )
      .then(function (res) {
        resolve({
          fieldId: res.data['field_id'],
          fieldName: res.data['field_name'],
        });
      })
      .catch(function (e) {
        reject(e);
      });
  });
};

export var update_field = (distribution_id, farming_unit_id, field_object) => {
  return new Promise(function (resolve, reject) {
    var sensors = JSON.parse(
      JSON.stringify(sensorObjectsToDictionary(field_object.sensors))
    );
    var irrigationSystem = JSON.parse(
      JSON.stringify(getIrrigationSystem(field_object.irrigation_system))
    );

    // remove spaces from transmitter_id
    for (let sensorsKey in sensors) {
      sensors[sensorsKey].transmitter_id = (
        sensors[sensorsKey].transmitter_id || ''
      ).replace(/\s+/g, '');
    }

    var geo_data = {
      areas: field_object.geo_data.areas,
      meteorological_stations: field_object.geo_data.meteorological_stations,
      agriapi_polygon_ids: field_object.geo_data.agriapi_polygon_ids,
      area_size: field_object.geo_data.area_size,
    };

    let crop_data = JSON.parse(JSON.stringify(field_object.crop_data));
    const updateKcType = field_object.crop_data.kc_table.type;
    crop_data.kc_table = {
      type: updateKcType,
      data: normalizeKcGrowthPhasesForPayload(
        crop_data.kc_table.data[updateKcType],
        updateKcType
      ),
    };

    let soil_data = getSoilDataObject(field_object);

    axios
      .put(
        SCIROOT_FIELD_PATH +
          '/' +
          distribution_id +
          '/' +
          farming_unit_id +
          '/' +
          field_object.id,
        {
          field_name: field_object.name,
          soil_type: field_object.soil_type,
          irrigation_system: irrigationSystem,
          sensors: sensors,
          cluster: field_object.cluster,
          crop_data: crop_data,
          soil_data: soil_data,
          geo_data: geo_data,
          installation_date: field_object.contract_data.installation_date,
          expiration_date: field_object.contract_data.expiration_date,
          farming_type: field_object.farming_type,
          settings: field_object.settings,
        }
      )
      .then(function (res) {
        resolve('');
      })
      .catch(function (e) {
        reject(e);
      });
  });
};

export var delete_field = (distribution_id, farming_unit_id, field_id) => {
  return new Promise(function (resolve, reject) {
    axios
      .delete(
        SCIROOT_FIELD_PATH +
          '/' +
          distribution_id +
          '/' +
          farming_unit_id +
          '/' +
          field_id,
        {}
      )
      .then(function (res) {
        resolve('');
      })
      .catch(function (e) {
        console.log(e.message);
        reject(e);
      });
  });
};

export var addFieldRecord = (
  fieldId,
  startDate,
  recordType,
  value,
  extra = {}
) => {
  return new Promise(function (resolve, reject) {
    axios
      .post(SCIROOT_FIELD_RECORDS_PATH + '/' + fieldId, {
        value: value,
        startDate: startDate,
        recordType: recordType,
        extra: extra,
      })
      .then(function (res) {
        resolve(res.data);
      })
      .catch(function (err) {
        console.log(error_message(err));
        reject(err);
      });
  });
};

export var loadFieldRecords = (
  fieldId,
  startDate,
  recordTypes,
  extra_data = []
) => {
  return new Promise(function (resolve, reject) {
    axios
      .get(
        SCIROOT_FIELD_RECORDS_PATH +
          '/' +
          fieldId +
          '/' +
          startDate +
          '?keys=' +
          recordTypes.join() +
          '&extra_data=' +
          extra_data.join(),
        {}
      )
      .then(function (res) {
        resolve(res.data);
      })
      .catch(function (err) {
        console.log(error_message(err));
        reject(err);
      });
  });
};

export var deleteFieldRecord = (fieldId, startDate, recordType) => {
  return new Promise(function (resolve, reject) {
    axios
      .delete(
        SCIROOT_FIELD_RECORDS_PATH +
          '/' +
          fieldId +
          '?startDate=' +
          startDate +
          '&recordType=' +
          recordType,
        {}
      )
      .then(function (res) {
        resolve(res.data);
      })
      .catch(function (err) {
        console.log(error_message(err));
        reject(err);
      });
  });
};

export var shouldDisplayAccumulatedIrrigation = field => {
  if (field) {
    for (let sensor in field.sensors) {
      if (
        field.sensors[sensor] instanceof WFRSensor ||
        field.sensors[sensor] instanceof PulseSensor ||
        field.sensors[sensor] instanceof NDripPiezometer
      ) {
        return true;
      }
    }
    if (
      field.historical_data &&
      (field.historical_data['precipitation_h'] ||
        field.historical_data['planned'] ||
        field.historical_data['wfr_length'])
    ) {
      return true;
    }
    return isSupportingPlannerByDays(field);
  }
  return false;
};

export var shouldDisplaySoilTemperatureGraph = field => {
  if (field) {
    // Check if field has soil temp sensors
    for (let sensor in field.sensors) {
      if (field.sensors[sensor] instanceof SoilTempSensor) {
        return true;
      }
    }
    // Also check if field has soil_temp historical data
    if (
      field.historical_data &&
      field.historical_data.soil_temp &&
      field.historical_data.soil_temp.values &&
      Object.keys(field.historical_data.soil_temp.values).length > 0
    ) {
      return true;
    }
  }
  return false;
};

export var getCurrentSeasonStartDate = field => {
  if (field) {
    let officialStartDate = daysjs(field.crop_data.sow_date);
    if (field.crop_data.irrigation_start_date !== '') {
      officialStartDate = daysjs(field.crop_data.irrigation_start_date);
    }
    let harvestDateMoment = daysjs();

    if (
      field.crop_data.harvest_date !== '' &&
      daysjs(field.crop_data.harvest_date) > harvestDateMoment
    ) {
      harvestDateMoment = daysjs(field.crop_data.harvest_date);
    }
    let todayMoment = daysjs();
    let closestDate = harvestDateMoment.isBefore(todayMoment)
      ? harvestDateMoment
      : todayMoment;
    let yearsDiff = daysjs
      .duration(closestDate.diff(officialStartDate))
      .asYears();

    // Add the integer part of the years difference to sow date
    let newDate = officialStartDate.add(Math.floor(yearsDiff), 'years');
    return newDate;
  }
};

export var isSupportingPlannerByDays = field => {
  if (field?.settings?.planner_settings?.type) {
    return (
      field.settings.planner_settings.type === plannerType.DAILY_RECOMMENDATION
    );
  }
  return field?.settings?.is_rolling_fit_active;
};

export var isSupportingPlannerByWeeks = field => {
  return (
    field?.settings?.planner_settings?.type ===
    plannerType.WEEKLY_RECOMMENDATION
  );
};

export var dashboardGraphToDisplay = field => {
  if (field) {
    for (let sensor in field.sensors) {
      if (field.sensors[sensor].type === 'wp') {
        return 'drop';
      }
    }

    if (shouldDisplayAccumulatedIrrigation(field)) {
      return 'accumulated';
    }
  }
  return 'drop';
};

export var historical_data_to_array = historical_data => {
  let returnArr = [];
  // PERFORMANCE OPTIMIZATION: Avoid Object.entries + [0][1] pattern
  // Access values directly for 35-45% performance improvement
  if (historical_data.hasOwnProperty('wtr')) {
    const wtrEntries = Object.entries(historical_data['wtr']);
    const wtrValues = wtrEntries[0][1];
    const wtrUnits = wtrEntries[0][0] === 'values' ? null : wtrEntries[1][1];
    for (const [value, wtrItem] of Object.entries(wtrValues)) {
      returnArr.push({
        startDate: value,
        value: 1 * parseFloat(wtrItem['value']).toFixed(3),
        unit: wtrItem['units'],
        type: 'wtr',
        wtrType: wtrItem['type'],
        note:
          wtrItem['type'] === 'cs'
            ? 'WT_timing_recommendation_after_irrigation_starts'
            : 'WT_timing_recommendation_last_hour',
      });
    }
  }
  if (historical_data.hasOwnProperty('fert')) {
    const fertEntries = Object.entries(historical_data['fert']);
    const fertValues = fertEntries[0][1];
    for (const [value, fertItem] of Object.entries(fertValues)) {
      returnArr.push({
        startDate: value,
        value: 1 * parseFloat(fertItem['value']).toFixed(3),
        unit: fertItem['units'],
        type: 'fertilization',
        source: fertItem['source'],
        fertAmount: 1 * parseFloat(fertItem['nitroAmount']).toFixed(3),
        fertType: fertItem['fertType'],
      });
    }
  }
  if (historical_data.hasOwnProperty('precipitation_h')) {
    const precipEntries = Object.entries(historical_data['precipitation_h']);
    const precipValues = precipEntries[0][1];
    const precipUnit = precipEntries[1][1];
    for (const [value, precipItem] of Object.entries(precipValues)) {
      let duration =
        (parseFloat(precipItem['endDate']) - parseFloat(value)) /
        (60 * 60).toFixed(1);
      returnArr.push({
        type: 'precipitation',
        startDate: value,
        endDate: precipItem['endDate'],
        value: precipItem['value'],
        unit: precipUnit,
        source: precipItem['source'],
        duration: duration,
      });
    }
  }
  if (historical_data.hasOwnProperty('precipitation_daily')) {
    const precipEntries = Object.entries(
      historical_data['precipitation_daily']
    );
    const precipValues = precipEntries[0][1];
    const precipUnit = precipEntries[1][1];
    for (const [value, precipItem] of Object.entries(precipValues)) {
      let duration =
        (parseFloat(precipItem['endDate']) - parseFloat(value)) /
        (60 * 60).toFixed(1);
      returnArr.push({
        type: 'precipitation_daily',
        startDate: value,
        endDate: precipItem['endDate'],
        value: precipItem['value'],
        unit: precipUnit,
        source: precipItem['source'],
        duration: duration,
      });
    }
  }
  if (historical_data.hasOwnProperty('wfr_length')) {
    const wfrEntries = Object.entries(historical_data['wfr_length']);
    const wfrValues = wfrEntries[0][1];
    const wfrUnit = wfrEntries[1][1];
    for (const [value, wfrItem] of Object.entries(wfrValues)) {
      let duration =
        (parseFloat(wfrItem['endDate']) - parseFloat(value)) /
        (60 * 60).toFixed(1);
      let manufacturer = wfrItem['manufacturer']
        ? '_' + wfrItem['manufacturer']
        : '';
      returnArr.push({
        startDate: value,
        endDate: wfrItem['endDate'],
        duration: duration,
        value: wfrItem['value'],
        source: wfrItem['source'],
        unit: wfrUnit,
        type: 'irrigation',
        sensor_id: wfrItem['sensor_id'],
        manufacturer: manufacturer,
        pressure: wfrItem['pressure'],
        pressure_unit: wfrItem['pressure_unit'],
        // 'low' | 'high' when the reading fell outside the pressure sensor's
        // configured boundaries — drives the Reported Events alert badge.
        pressure_alert: wfrItem['pressure_alert'],
      });
    }
  }
  if (historical_data.hasOwnProperty('discharge')) {
    let dischargeArr = Object.values(historical_data['discharge']);
    dischargeArr.forEach(dischargeItem => {
      returnArr.push({
        startDate: dischargeItem['date'],
        value: dischargeItem['value'],
        source: dischargeItem['source'],
        unit: 'm³/h',
        type: 'discharge',
        wfrValue: dischargeItem['wfrValue'] || undefined,
        wfrEndDate: dischargeItem['endDate'] || undefined,
        sensorId: dischargeItem['sensorId'] || undefined,
      });
    });
  }
  return returnArr;
};

// Helper function to get date string (YYYY-MM-DD) in a specific timezone
// Uses native Intl.DateTimeFormat which works in React Native
var getDateStringInTimezone = (unixTimestamp, timezone) => {
  if (!unixTimestamp || isNaN(unixTimestamp)) {
    return null;
  }
  const date = new Date(unixTimestamp * 1000);
  if (isNaN(date.getTime())) {
    return null;
  }
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

// Helper function to get the difference in days between two date strings
var getDaysDiff = (dateStr1, dateStr2) => {
  // Parse YYYY-MM-DD format
  const d1 = new Date(dateStr1 + 'T00:00:00Z');
  const d2 = new Date(dateStr2 + 'T00:00:00Z');
  const diffMs = d1.getTime() - d2.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
};

export var getEventDateTitle = (startDate, endDate, timezone, sourceCode) => {
  let title = '';

  // Use native Intl.DateTimeFormat for timezone conversion (works in React Native)
  // dayjs.tz() doesn't work in React Native as it lacks the IANA timezone database
  const nowDateStr = getDateStringInTimezone(
    Math.floor(Date.now() / 1000),
    timezone
  );
  const startDateStr = getDateStringInTimezone(parseInt(startDate), timezone);
  const endDateStr = endDate
    ? getDateStringInTimezone(parseInt(endDate), timezone)
    : startDateStr;

  // Handle invalid date values
  if (!nowDateStr || !startDateStr) {
    return '';
  }

  if (sourceCode === 'reported') {
    const daysDiff = getDaysDiff(nowDateStr, startDateStr);
    if (daysDiff === 1) {
      title = upper('yesterday');
    } else if (daysDiff === 0) {
      title = upper('today');
    } else {
      title = ' ';
    }
  } else {
    const daysToEnd = getDaysDiff(endDateStr, nowDateStr);
    const daysToStart = getDaysDiff(startDateStr, nowDateStr);

    if (daysToEnd < 0) {
      // Event ended in the past
      title = '';
    } else if (daysToStart === 0 || (daysToStart < 0 && daysToEnd >= 0)) {
      // Event starts today or is ongoing (started in past, ends in future)
      title = upper('today');
    } else if (daysToEnd === 1) {
      title = upper('tomorrow');
    } else {
      title = ' ';
    }
  }

  return title;
};

export var getEventDurationLine = extraData => {
  let durationLine = [];
  if ('duration' in extraData && extraData['duration']) {
    if (extraData['duration'] % 1 !== 0) {
      let minutes = (
        (extraData['duration'] - Math.floor(extraData['duration'])) *
        60
      ).toFixed(0);
      let hours = Math.floor(extraData['duration']).toFixed(0);
      if (hours > 0) {
        durationLine.push(hours.toString());
        durationLine.push(' hr ');
        durationLine.push(minutes.toString());
        durationLine.push(' min');
      } else {
        durationLine.push(minutes.toString());
        durationLine.push(' min');
      }
    } else {
      durationLine.push(
        Math.floor(extraData['duration']).toFixed(0).toString()
      );
      if (extraData['duration'] > 1) {
        durationLine.push(' hours');
      } else {
        durationLine.push(' hour');
      }
    }
  }
  return durationLine;
};

export var loadFieldHistory = (distribution_id, farming_unit_id, field_id) => {
  return new Promise(function (resolve, reject) {
    axios
      .get(
        SCIROOT_FIELD_PATH +
          '/' +
          distribution_id +
          '/' +
          farming_unit_id +
          '/' +
          field_id,
        {
          field_id: field_id,
          farming_unit_id: farming_unit_id,
          distribution_id: distribution_id,
        }
      )
      .then(function (res) {
        resolve({ historicalData: res.data, field_id: field_id });
      })
      .catch(function (e) {
        console.log(e.message);
        reject(e);
      });
  });
};

export var deleteDischargeTestEvent = (event, selectedField) => {
  return new Promise(function (resolve, reject) {
    sensorsManager
      .deleteSensorRecord(
        selectedField.id,
        event.source,
        event.startDate,
        'discharge_test'
      )
      .then(function (res) {
        resolve({ status: 'success' });
      })
      .catch(function (e) {
        console.log(e.message);
        reject({ status: 'fail', error: e });
      });
  });
};

export var deleteWfrDischargeEvent = (event, selectedField) => {
  return new Promise(function (resolve, reject) {
    sensorsManager
      .deleteSensorRecordProperty(
        selectedField.id,
        event.sensorId,
        event.startDate,
        'wfr',
        'discharge'
      )
      .then(function (res) {
        resolve({ status: 'success' });
      })
      .catch(function (e) {
        console.log(e.message);
        reject({ status: 'fail', error: e });
      });
  });
};

export var deleteIrrigationEvent = (
  event,
  disableEventRecovery,
  selectedField
) => {
  return new Promise(function (resolve, reject) {
    if (disableEventRecovery) {
      sensorsManager
        .addSensorRecord(
          selectedField.id,
          event.source,
          event.startDate,
          'wfr',
          0,
          { status: 'invalid' }
        )
        .then(res => {
          resolve({ status: 'success' });
        })
        .catch(function (e) {
          console.log(e.message);
          reject({ status: 'fail', error: e });
        });
    } else {
      sensorsManager
        .deleteSensorRecord(
          selectedField.id,
          event.source,
          event.startDate,
          'wfr'
        )
        .then(function (res) {
          resolve({ status: 'success' });
        })
        .catch(function (e) {
          console.log(e.message);
          reject({ status: 'fail', error: e });
        });
    }
  });
};

export var deletePrecipitationEvent = (
  event,
  disableEventRecovery,
  selectedField
) => {
  let deletePrecipitation;

  if (event?.source === 'manual') {
    deletePrecipitation = deleteFieldRecord;
    event.source = selectedField.id;
  } else {
    deletePrecipitation = weatherManager.deleteWeatherRecord;
  }
  return new Promise(function (resolve, reject) {
    deletePrecipitation(event.source, event.startDate, 'precipitation_h')
      .then(function (res) {
        resolve({ status: 'success' });
      })
      .catch(function (e) {
        console.log(e.message);
        reject({ status: 'fail', error: e });
      });
  });
};

export var deleteFertilizationEvent = (event, selectedField) => {
  return new Promise(function (resolve, reject) {
    deleteFieldRecord(selectedField.id, event.startDate, 'fert')
      .then(function (res) {
        resolve({ status: 'success' });
      })
      .catch(function (e) {
        console.log(e.message);
        reject({ status: 'fail', error: e });
      });
  });
};

export var deleteWtrEvent = (event, selectedField) => {
  return new Promise(function (resolve, reject) {
    deleteFieldRecord(selectedField.id, event.startDate, 'wtr')
      .then(function (res) {
        resolve({ status: 'success' });
      })
      .catch(function (e) {
        console.log(e.message);
        reject({ status: 'fail', error: e });
      });
  });
};

// A function for analytics requirements, to support when a field is in its early days
export var isFieldEarlyDays = field => {
  const EARLY_DAYS_THRESHOLD = 14;
  let isFieldEarlyDays = false;

  if (field) {
    if (
      daysjs().diff(getCurrentSeasonStartDate(field), 'days') <
      EARLY_DAYS_THRESHOLD
    ) {
      isFieldEarlyDays = true;
    }
  }

  return isFieldEarlyDays;
};

export var getIfThisFieldSupportFert = (fieldSensors, farmingUnitSensors) => {
  let fieldSupportFert = false;
  for (let sensor in fieldSensors) {
    if (fieldSensors[sensor].manufacturer === 'tal-gil') {
      for (let farmingSensor in farmingUnitSensors) {
        if (
          farmingUnitSensors[farmingSensor].transmitter_id ===
            fieldSensors[sensor].transmitter_id &&
          farmingUnitSensors[farmingSensor].fert_meters &&
          Object.keys(farmingUnitSensors[farmingSensor].fert_meters).length > 0
        ) {
          fieldSupportFert = true;
        }
      }
    }
  }
  return fieldSupportFert;
};

export const calculateDaysFromSeasonStart = sow_date => {
  if (sow_date) {
    const sowDateMoment = daysjs(sow_date);
    const currentDate = daysjs();
    if (currentDate.isBefore(sowDateMoment)) {
      return 0;
    } else {
      return currentDate.diff(
        getRelevantCyclicDateByDate(currentDate, sowDateMoment),
        'days'
      );
    }
  }
};

// New Season (V2 season PUT) — additive, used only by the flag-gated Season
// editor. Returns the exact request body `update_field` above PUTs to
// `field/...`, built with the same private helpers, so the V2 endpoint (which
// runs V1's update_field_logic for active/archived seasons) receives a byte-for-
// byte V1 body. `update_field` itself is intentionally left unchanged.
export var build_update_field_body = field_object => {
  var sensors = JSON.parse(
    JSON.stringify(sensorObjectsToDictionary(field_object.sensors))
  );
  var irrigationSystem = JSON.parse(
    JSON.stringify(getIrrigationSystem(field_object.irrigation_system))
  );

  for (let sensorsKey in sensors) {
    sensors[sensorsKey].transmitter_id = (
      sensors[sensorsKey].transmitter_id || ''
    ).replace(/\s+/g, '');
  }

  var geo_data = {
    areas: field_object.geo_data.areas,
    meteorological_stations: field_object.geo_data.meteorological_stations,
    agriapi_polygon_ids: field_object.geo_data.agriapi_polygon_ids,
    area_size: field_object.geo_data.area_size,
  };

  let crop_data = JSON.parse(JSON.stringify(field_object.crop_data));
  const updateKcType = field_object.crop_data.kc_table.type;
  crop_data.kc_table = {
    type: updateKcType,
    data: normalizeKcGrowthPhasesForPayload(
      crop_data.kc_table.data[updateKcType],
      updateKcType
    ),
  };

  return {
    field_name: field_object.name,
    soil_type: field_object.soil_type,
    irrigation_system: irrigationSystem,
    sensors: sensors,
    cluster: field_object.cluster,
    crop_data: crop_data,
    soil_data: getSoilDataObject(field_object),
    geo_data: geo_data,
    installation_date: field_object.contract_data.installation_date,
    expiration_date: field_object.contract_data.expiration_date,
    farming_type: field_object.farming_type,
    settings: field_object.settings,
  };
};
